import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, ArrowLeft, Truck, Package, PartyPopper, Wrench, Coffee,
  MessageSquare, Instagram, Calendar, Star, Bike, MapPin, ExternalLink,
} from "lucide-react";
import Squiggle from "@/components/Squiggle";
import { EVENTS_PHOTOS } from "@/config/site";
import { useSettings } from "@/context/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKLY_ICON_MAP = {
  wrench: Wrench,
  coffee: Coffee,
  calendar: Calendar,
  star: Star,
  party: PartyPopper,
  bike: Bike,
};

const BADGE_STYLE_MAP = {
  red: "bg-tomato-500 text-white",
  dark: "bg-leaf-900 text-white",
  green: "bg-leaf-600 text-white",
};

const ICON_STYLE_MAP = {
  wrench: "bg-tomato-500 text-white",
  coffee: "bg-leaf-900 text-sun-200",
  calendar: "bg-leaf-600 text-white",
  star: "bg-sun-400 text-leaf-900",
  party: "bg-tomato-500 text-white",
  bike: "bg-leaf-900 text-white",
};

function PhotoCarousel({ photos }) {
  const [index, setIndex] = useState(0);
  const total = photos.length;
  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = () => setIndex((i) => (i - 1 + total) % total);

  useEffect(() => {
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [next]);

  const photo = photos[index % total];

  return (
    <div data-testid="events-carousel" className="relative">
      <div className="relative overflow-hidden rounded-[2rem] border-4 border-white bg-leaf-900 shadow-[0_20px_60px_rgba(36,33,32,0.2)]">
        <img
          key={photo.src}
          src={photo.src}
          alt={photo.caption}
          data-testid="events-carousel-image"
          className="fade-up h-[380px] w-full object-cover sm:h-[540px]"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-leaf-900/85 to-transparent p-5 pt-14">
          <p data-testid="events-carousel-caption" className="font-display text-lg font-semibold text-white sm:text-xl">
            {photo.caption}
          </p>
          <p className="mt-1 text-xs font-bold uppercase tracking-widest text-sun-200">
            {index + 1} / {total}
          </p>
        </div>
        <button
          data-testid="events-carousel-prev"
          onClick={prev}
          aria-label="Previous photo"
          className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 text-leaf-900 shadow-lg transition-all hover:bg-white active:scale-95"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <button
          data-testid="events-carousel-next"
          onClick={next}
          aria-label="Next photo"
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 text-leaf-900 shadow-lg transition-all hover:bg-white active:scale-95"
        >
          <ArrowRight className="h-5 w-5" />
        </button>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        {photos.map((p, i) => (
          <button
            key={p.src}
            data-testid={`events-carousel-dot-${i}`}
            onClick={() => setIndex(i)}
            aria-label={`Go to photo ${i + 1}`}
            className={`h-2.5 rounded-full transition-all ${
              i === index ? "w-7 bg-leaf-600" : "w-2.5 bg-sun-400 hover:bg-sun-500"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

const POPUP_OPTIONS = [
  {
    icon: Truck,
    title: "Full Trailer Pop-Up",
    desc: "Our fully-equipped shop trailer rolls to your race, group ride, or festival. Workstands, full tool bench, parts, and a mechanic ready to keep every bike rolling all day.",
    bullets: ["Complete mobile workshop", "Races, festivals & big group rides", "All-day on-site support"],
  },
  {
    icon: Package,
    title: "Light Onsite Kit",
    desc: "Smaller gathering? We bring a compact kit — stand, essential tools, and common spares — perfect for club meetups, school events, and neighborhood rides.",
    bullets: ["Compact setup, quick to deploy", "Club meetups & school events", "Safety checks & quick fixes"],
  },
];

export default function Events() {
  const { settings } = useSettings();
  const photos = settings.events_photos?.length ? settings.events_photos : EVENTS_PHOTOS;
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/events`).then(({ data }) => {
      if (alive) setUpcoming(Array.isArray(data) ? data : []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="page-events">
      <section className="relative overflow-hidden px-4 pt-12 pb-8 sm:px-8 md:px-12 md:pt-16">
        <div className="pointer-events-none absolute -top-20 right-0 h-80 w-80 rounded-full bg-sun-200/60 blur-3xl" />
        <div className="relative mx-auto max-w-6xl">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border-2 border-leaf-200 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-leaf-700">
            <PartyPopper className="h-3.5 w-3.5" /> Pop-ups · Races · Group Rides
          </div>
          <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tight text-leaf-900 sm:text-6xl md:text-7xl">
            The shop that <span className="marker-underline italic text-leaf-600">comes to you.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-leaf-900/80">
            From race-day support to café wrench days, Last Stop pops up wherever
            St. Louis rides. Book the full trailer or the light kit for your next event.
          </p>
        </div>
      </section>

      <section className="px-4 py-8 sm:px-8 md:px-12">
        <div className="mx-auto max-w-6xl">
          <PhotoCarousel photos={photos} />
        </div>
      </section>

      <Squiggle className="px-4 mt-4" />

      <section className="px-4 py-14 sm:px-8 md:px-12" data-testid="events-weekly-section">
        <div className="mx-auto max-w-6xl">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border-2 border-tomato-200 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-tomato-600">
            Every Week · Free · Open Shop
          </div>
          <h2 className="font-display text-3xl font-bold text-leaf-900 sm:text-4xl">
            Weekly at the trailer
          </h2>
          <p className="mt-3 max-w-2xl text-leaf-900/75">
            Two standing gatherings you can just show up to — no booking needed.
          </p>

          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {(settings.weekly_events || [])
              .filter((w) => !w.hidden)
              .map((w) => {
                const IconCmp = WEEKLY_ICON_MAP[w.icon] || Calendar;
                const iconCls = ICON_STYLE_MAP[w.icon] || ICON_STYLE_MAP.calendar;
                const badgeCls = BADGE_STYLE_MAP[w.badge_color] || BADGE_STYLE_MAP.red;
                return (
                  <article
                    key={w.id}
                    data-testid={`events-weekly-${w.id}`}
                    className="relative overflow-hidden rounded-3xl border-2 border-leaf-200 bg-white p-7 transition-all hover:-translate-y-1 hover:border-leaf-300 hover:shadow-xl"
                  >
                    <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-sun-200/60 blur-2xl" />
                    <div className="relative">
                      <div className="flex items-start justify-between gap-3">
                        <span className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl ${iconCls}`}>
                          <IconCmp className="h-7 w-7" />
                        </span>
                        {w.schedule && (
                          <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${badgeCls}`}>
                            {w.schedule}
                          </span>
                        )}
                      </div>
                      <h3 className="mt-5 font-display text-2xl font-bold text-leaf-900">
                        {w.title}
                      </h3>
                      {w.description && (
                        <p className="mt-2 text-leaf-900/75">{w.description}</p>
                      )}
                      {Array.isArray(w.bullets) && w.bullets.length > 0 && (
                        <ul className="mt-4 space-y-2 text-sm text-leaf-900/80">
                          {w.bullets.map((b, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-tomato-500" />
                              {b}
                            </li>
                          ))}
                        </ul>
                      )}
                      {(w.rsvp_instagram || w.rsvp_sms) && (
                        <div className="mt-5 flex flex-wrap gap-2">
                          {w.rsvp_instagram && (
                            <a
                              href={w.rsvp_instagram}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`events-weekly-${w.id}-instagram`}
                              className="inline-flex items-center gap-2 rounded-full bg-leaf-900 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition-all hover:-translate-y-0.5 hover:bg-leaf-700"
                            >
                              <Instagram className="h-3.5 w-3.5" /> RSVP on Instagram
                            </a>
                          )}
                          {w.rsvp_sms && (
                            <a
                              href={`sms:${w.rsvp_sms}`}
                              data-testid={`events-weekly-${w.id}-sms`}
                              className="inline-flex items-center gap-2 rounded-full border-2 border-leaf-900 bg-white px-4 py-2 text-xs font-bold uppercase tracking-wider text-leaf-900 transition-all hover:-translate-y-0.5 hover:bg-leaf-50"
                            >
                              <MessageSquare className="h-3.5 w-3.5" /> Text to RSVP
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
          </div>
        </div>
      </section>

      {upcoming.length > 0 && (
        <section
          data-testid="events-upcoming-section"
          className="px-4 pb-6 sm:px-8 md:px-12"
        >
          <div className="mx-auto max-w-6xl">
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border-2 border-leaf-200 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-leaf-700">
              <Calendar className="h-3 w-3" /> What&apos;s coming up
            </div>
            <h2 className="font-display text-3xl font-bold text-leaf-900 sm:text-4xl">
              Upcoming events
            </h2>
            <p className="mt-3 max-w-2xl text-leaf-900/75">
              Special pop-ups, race weekends, group rides, and one-off closures.
            </p>

            <ul className="mt-8 space-y-3">
              {upcoming.map((ev) => {
                const d = new Date(ev.date + "T00:00:00");
                const monthShort = d.toLocaleString("en-US", { month: "short" });
                const day = d.getDate();
                const weekday = d.toLocaleString("en-US", { weekday: "short" });
                return (
                  <li
                    key={ev.id}
                    data-testid={`events-upcoming-${ev.id}`}
                    className="flex flex-wrap items-start gap-4 rounded-2xl border-2 border-leaf-100 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-leaf-300 hover:shadow-md sm:p-5"
                  >
                    <div className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-2xl bg-tomato-500 text-white">
                      <span className="text-[10px] font-bold uppercase tracking-widest">{monthShort}</span>
                      <span className="font-display text-2xl font-black leading-none">{day}</span>
                      <span className="text-[9px] font-bold uppercase tracking-wider opacity-80">{weekday}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-display text-xl font-bold text-leaf-900">{ev.title}</h3>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-leaf-900/70">
                        {ev.time && <span>{ev.time}</span>}
                        {ev.location && (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" /> {ev.location}
                          </span>
                        )}
                      </div>
                      {ev.description && (
                        <p className="mt-2 whitespace-pre-wrap text-sm text-leaf-900/85">
                          {ev.description}
                        </p>
                      )}
                      {ev.link && (
                        <a
                          href={ev.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`events-upcoming-${ev.id}-link`}
                          className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-leaf-700 hover:underline"
                        >
                          More info <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      <Squiggle className="px-4" />

      <section className="px-4 py-14 sm:px-8 md:px-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="font-display text-3xl font-bold text-leaf-900 sm:text-4xl">
            Two ways to bring us out
          </h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {POPUP_OPTIONS.map((o) => (
              <article
                key={o.title}
                data-testid={`events-option-${o.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-3xl border-2 border-leaf-200 bg-white p-7 transition-all hover:-translate-y-1 hover:border-leaf-300 hover:shadow-xl"
              >
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-leaf-100 text-leaf-600">
                  <o.icon className="h-7 w-7" />
                </span>
                <h3 className="mt-4 font-display text-2xl font-bold text-leaf-900">{o.title}</h3>
                <p className="mt-2 text-leaf-900/75">{o.desc}</p>
                <ul className="mt-4 space-y-2 text-sm text-leaf-900/80">
                  {o.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-tomato-500" />
                      {b}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-8 md:px-12">
        <div
          data-testid="events-cta-band"
          className="mx-auto max-w-5xl rounded-[2.5rem] bg-leaf-900 p-8 text-center text-white sm:p-14"
        >
          <PartyPopper className="mx-auto h-8 w-8 text-sun-300" />
          <h2 className="mt-4 font-display text-4xl font-bold leading-tight sm:text-5xl">
            Planning a ride, race, or gathering?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-leaf-50/85">
            Tell us the date and headcount. We&apos;ll bring the wrenches.
          </p>
          <Link
            to="/contact#booking"
            data-testid="events-band-cta"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-tomato-500 px-7 py-3.5 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-tomato-600"
          >
            Request the pop-up <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
